import { styled } from '@compiled/react';
import type { FC } from 'react';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import ButtonGroup from '@atlaskit/button/button-group';
import { Checkbox as AkCheckbox } from '@atlaskit/checkbox';
import ModalDialog, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@atlaskit/modal-dialog';

import type { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition';
import type { DialogsContainerProps } from '@confluence/dialogs';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FooterContainer = styled.div<{
	hasCheckbox?: boolean;
}>({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: `${({ hasCheckbox }) => (hasCheckbox ? '100%' : undefined)}`,
});

export type PublicLinkDialogBaseProps = DialogsContainerProps & {
	title: React.ReactNode;
	children: React.ReactNode;
	onYesButtonClick: () => void;
	yesButtonText: React.ReactNode | string;
	onCancelButtonClick?: () => void;
	noButtonText?: React.ReactNode | string;
	screenName: string;
	onClose: () => void;
	triggerScreen?: string;
	edition?: ConfluenceEdition | null;
	isWarning?: boolean;
	showOptOutCheckbox?: boolean;
	setKey?: (isChecked: boolean) => void;
	onSecondaryActionButtonClick?: () => void;
	secondaryActionButtonText?: React.ReactNode | string;
};

export const PublicLinkDialogBase: FC<PublicLinkDialogBaseProps> = ({
	title,
	children,
	onYesButtonClick,
	onCancelButtonClick,
	yesButtonText,
	noButtonText,
	triggerScreen,
	screenName,
	edition,
	onClose,
	isWarning = false,
	showOptOutCheckbox = false,
	setKey,
	onCloseComplete = () => {},
	onSecondaryActionButtonClick,
	secondaryActionButtonText,
}) => {
	const [isChecked, setIsChecked] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: screenName,
				attributes: {
					...(triggerScreen && { source: triggerScreen }),
					...(edition && { edition }),
				},
			},
		}).fire();
	}, [createAnalyticsEvent, screenName, triggerScreen, edition]);

	const onOptOutCheckboxChange = () => {
		setIsChecked(!isChecked);
	};

	const handleYesButtonClick = () => {
		onYesButtonClick();
		onClose();
		if (setKey) setKey(isChecked);
	};

	const handleSecondaryActionButtonClick = () => {
		onSecondaryActionButtonClick?.();
		onClose();
	};

	const handleCancelButtonClick = () => {
		onCancelButtonClick?.();
		onClose();
	};

	return (
		<ModalDialog
			width="medium"
			onClose={handleCancelButtonClick}
			onCloseComplete={onCloseComplete}
			testId="public-links-friction"
		>
			<ModalHeader>
				<ModalTitle appearance={isWarning ? 'warning' : undefined}>{title}</ModalTitle>
			</ModalHeader>

			<ModalBody>{children}</ModalBody>

			<ModalFooter>
				<FooterContainer hasCheckbox={showOptOutCheckbox}>
					{showOptOutCheckbox && (
						<AkCheckbox
							isChecked={isChecked}
							label={
								<FormattedMessage
									id="public-links.checkbox.dont-show-again"
									defaultMessage="Don’t show me this again"
									description="Label for checkbox where users can choose not to show the dialog in the future"
								/>
							}
							name="dont-show-again-checkbox"
							onChange={onOptOutCheckboxChange}
						/>
					)}
					<ButtonGroup>
						{noButtonText && (
							<Button appearance="subtle" onClick={handleCancelButtonClick}>
								{noButtonText}
							</Button>
						)}
						{secondaryActionButtonText && (
							<Button onClick={handleSecondaryActionButtonClick}>
								{secondaryActionButtonText}
							</Button>
						)}
						<Button appearance={isWarning ? 'warning' : 'primary'} onClick={handleYesButtonClick}>
							{yesButtonText}
						</Button>
					</ButtonGroup>
				</FooterContainer>
			</ModalFooter>
		</ModalDialog>
	);
};
